import { useState } from 'react';
import { colour, radius, space, text } from '../design/tokens.ts';
import { Card, Field, Page, TextInput } from '../components/basics.tsx';
import { CouldNotLoad, Loading } from '../components/states.tsx';
import { api, query } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';

/**
 * Screen 16: the glossary, the status legend, and the questions people ask.
 *
 * The text comes from the server (`src/content/help.ts`) rather than being
 * written again here — "stale" means one exact thing in Maestro, and a second
 * definition written in passing on a help page is how a team ends up arguing
 * about what the board is telling them.
 */

interface Help {
  terms: { term: string; says: string; see_also?: string[] }[];
  statuses: { key: string; word: string; colour: string; says: string; where: string }[];
  faqs: { question: string; answer: string }[];
  total: number;
  searched: boolean;
  counts: { terms: number; statuses: number; faqs: number };
  about_colour: string;
}

export function Help() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const { state, reload } = useLoad<Help>(() => api.get(`/api/help${query({ q })}`), [q]);
  const help = state.data;

  return (
    <Page
      eyebrow="MAESTRO · HELP AND GLOSSARY"
      title="Help"
      description="The words Maestro uses, what each status means, and the things people ask."
    >
      <Card>
        <Field label="Search the help" grow>
          <TextInput value={q} onChange={setQ} placeholder="stale, proxy, who can mark things good…" />
        </Field>
        {help?.searched && (
          <span style={{ ...text.small, color: colour.inkSoft }}>
            {help.total === 0 ? 'Nothing matches that.' : `${help.total} matches.`}
          </span>
        )}
      </Card>

      {state.status === 'loading' && !help && <Loading says="Loading the help…" rows={4} />}
      {state.status === 'failed' && !help && (
        <CouldNotLoad what="the help" error={state.error} onRetry={reload} />
      )}

      {help && (
        <>
          <Card title="What each status means" aside={help.about_colour}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
                gap: space.control
              }}
            >
              {help.statuses.map((status) => (
                <div key={status.key} style={{ display: 'flex', gap: space.tight, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      marginTop: 3,
                      borderRadius: 3,
                      background: status.colour,
                      border: `1px solid ${colour.edgeStrong}`,
                      flex: 'none'
                    }}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={text.smallStrong}>{status.word}</span>
                    <span style={{ ...text.note, color: colour.inkSoft }}>{status.says}</span>
                    <span style={{ ...text.note, color: colour.inkFaint }}>{status.where}</span>
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="The words" aside={`${help.counts.terms} of them`}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
                gap: space.control
              }}
            >
              {help.terms.map((term) => (
                <div key={term.term} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={text.smallStrong}>{term.term}</span>
                  <span style={{ ...text.note, color: colour.inkSoft }}>{term.says}</span>
                  {term.see_also && term.see_also.length > 0 && (
                    <span style={{ ...text.note, color: colour.inkFaint }}>
                      See also: {term.see_also.join(', ')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card title="Questions people ask">
            {help.faqs.map((faq) => (
              <div key={faq.question} style={{ borderTop: `1px solid ${colour.edgeSoft}`, paddingTop: space.tight }}>
                <button
                  type="button"
                  onClick={() => setOpen(open === faq.question ? null : faq.question)}
                  aria-expanded={open === faq.question}
                  style={{
                    width: '100%',
                    minHeight: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: space.tight,
                    border: 'none',
                    borderRadius: radius.control,
                    background: 'transparent',
                    color: colour.ink,
                    font: text.bodyStrong.font,
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                >
                  {faq.question}
                  <span aria-hidden="true" style={{ color: colour.inkMuted }}>
                    {open === faq.question ? '−' : '+'}
                  </span>
                </button>
                {open === faq.question && (
                  <p style={{ margin: '4px 0 12px', ...text.body, color: colour.inkSoft }}>{faq.answer}</p>
                )}
              </div>
            ))}
          </Card>
        </>
      )}
    </Page>
  );
}
